import { EvaButtonSize, EvaButtonVariant } from 'eva-components/types/eva-types';
import { GlobaPosition, GlobaSize, SofiaMode } from '../types';
import { AccommodationOfferDto, AccommodationRoomPackAvailabilityDto } from './accommodations/types';
import { TripDaysDto } from './trip-planner/types';

export type MessageType = 'ASSISTANT' | 'SEND_ERROR' | 'USER' | 'OFFLINE' | 'REQUIRED_LOGIN';

export interface RequestContext {
  sandbox: boolean;
  userId?: string;
  temporalUserId?: string;
  externalUserId?: string;
  assistantId: string;
  mode: SofiaMode;
  locale: string;
}

export interface ChatContext {
  channel: string;
  site: string;
  assistantId: string;
  brandId: string;
  location?: {
    latitude: number;
    longitude: number;
  };
}

// Objeto a enviar en el body de la request de createChat
export interface CreateChatBody {
  xLocale?: string;
  trackerId?: string;
  config?: string;
  newChat?: boolean;
  context: ChatContext;
  externalUserId?: string;
  temporalUserId?: string;
  auth?: AuthAPI;
}

export interface GetChatParams {
  userId: string;
  chatId: string;
}

//Objeto a enviar en el body de la request de createChat/sendMessage
export interface AuthKeyAPI {
  tokenName: string;
  tokenValue: string;
}

export type LevelAuth = '1' | '2';

export type KeyLevelsAuth = { [k in LevelAuth]?: AuthKeyAPI };

//Objeto a enviar en el body de la request de createChat/sendMessage
export interface AuthAPI {
  keyLevels: KeyLevelsAuth;
}

// Objeto a enviar en el body de la request de sendMessage
export interface SendMessageBody {
  type: MessageType;
  text: string;
  context?: ChatContext;
  actionId?: string;
  params?: any;
  temporalUserId: string;
  externalUserId?: string;
  auth?: AuthAPI;
}

export interface FeedbackMessageBody {
  action: 'LIKE' | 'DISLIKE';
  text?: string;
}

export interface FeedbackFormBody {
  score: number;
  question1: string;
}

interface BoardPosition {
  gridRow?: string;
  gridColumn?: string;
}

interface BoardTitle {
  title?: string;
  description?: string;
  headerIcon?: string;
}

interface BoardFooter {
  footerTitle?: string;
  footerItems?: string[];
}

export interface GenericBoard<T> extends BoardPosition, BoardTitle, BoardFooter {
  items?: T[];
  filters?: FilterDTO[];
}

export type CategoryContext = 'OTHER' | 'GREETINGS' | 'INSPIRATION' | 'SPECIFIC' | 'PRODUCT' | 'NO_CATEGORY' | 'ISSUES' | 'GENERAL_INFO' | 'TRIP_PLANNER';

export interface SuggestionDto {
  label: string;
  action: string;
  icon?: string;
}

export interface MessageSuggestionsDto {
  title?: string;
  type: 'PROMPT' | 'HREF';
  options: SuggestionDto[];
  hideOnSelection: boolean;
  showMore?: ShowMoreDto;
  renderMode: 'COLUMN' | 'ROW';
  showOnChat?: boolean;
}

export interface ShowMoreDto {
  maxItemsToShow: number;
}

export type MessageComponentData = AllDtos | GenericBoard<any>;
export type MessageComponentName =
  | 'chat-aftersale'
  | 'chat-cards'
  | 'chat-date-suggestion'
  | 'chat-fare-suggestion'
  | 'chat-generic-cards'
  | 'chat-images-slider-wrapper'
  | 'chat-issues'
  | 'chat-itinerary-map'
  | 'chat-landing-items'
  | 'chat-map-view'
  | 'chat-product-cards'
  | 'chat-reels'
  | 'chat-reservation-detail'
  | 'chat-suggestion-destinations'
  | 'chat-suggestion-prompts'
  | 'chat-texts'
  | 'chat-trip-planner';

export interface MessageComponentDto {
  data: MessageComponentData;
  board?: MessageBoardDto;
  component: MessageComponentName;
}

export interface MessageContentDto {
  components?: MessageComponentDto[];
}

export interface MessageContextDto {
  category?: CategoryContext;
  filters: FilterDTO[];
  debug?: DebugDTO;
}

export interface DebugDTO {
  tools?: ToolAndParamsDTO[];
  metadata?: FlowMetadataDTO;
  attributes?: Map<String, String>;
}

export interface ToolAndParamsDTO {
  tool: string;
  params: string;
  step: number;
}

export interface FlowMetadataDTO {
  conversation?: ConversationMetadataDTO;
  prompts?: PromptsMetadataDTO;
}

export interface ConversationMetadataDTO {
  active: string[];
  enabled: string[];
}

export interface PromptsMetadataDTO {
  start?: string;
  followup?: string;
  compose?: string;
}

export interface FilterDTO {
  id: string;
  name: string;
  description: string;
  products: string[];
  value: FilterValueDTO;
  options?: FilterValueDTO[];
  removeFilterPrompt: string;
}

interface FilterValueDTO {
  type: string;
  id: string;
  value: string;
}

interface DestinationDto {
  title: string;
  subtitle: string;
  images: ImageDto[];
  description: string;
  suggestion?: string;
  suggestions?: { suggestion: string; icon?: string; text: string; variant: string }[];
}

interface SuggestionItemDto {
  href?: string;
  title?: string;
  icon?: string;
  subtitle: string;
  isLarge: boolean;
  text: string;
}

interface LandingItemDto {
  title: string;
  description: string;
  image?: ImageDto;
  link?: string;
  suggestion?: string;
  type?: 'flights' | 'hotels';
}

interface PriceTrendValueDto {
  value: number;
  description: string;
}

interface FlightPriceTrendDto {
  currency: string;
  min: PriceTrendValueDto;
  average: PriceTrendValueDto;
  months: {
    name: string;
    month: number;
    year: number;
    suggestion: string;
    min?: PriceTrendValueDto;
  }[];
}

interface GenericCardDto {
  title: string;
  subtitle?: string;
  body: string;
  buttons?: {
    items: (HrefButtonDto | PromptButtonDto | ActionButtonDto)[];
    align?: 'left' | 'right';
  };
}

interface PriceDto {
  amount: string;
  currency: string;
  description?: string;
}

interface FormattedDateDto {
  weekDay: string;
  day: number;
  month: string;
}

interface DateSuggestionsDto {
  price: PriceDto;
  nights: string;
  dateFrom: FormattedDateDto;
  dateTo: FormattedDateDto;
  suggestion: string;
  alternativeTarget?: string;
  discountPercentage?: number;
}

interface IllustrationDto {
  name: string;
}

interface CardDto {
  title: string;
  icon?: string;
  image?: ImageDto;
  illustration?: IllustrationDto;
  link?: string;
  suggestion?: string;
  button: {
    text: string;
    icon?: string;
  };
  action?: ActionDto;
  driver?: DriverDto;
}
export type PoiTypeDto = 'ORIGIN' | 'CITY' | 'OFFER' | 'POI';
export interface PoiMetaData {
  description?: string;
  subtitle?: string;
  priority?: number;
  icon?: string;
  suggestion?: string;
  image?: ImageTypeDto;
  price?: {
    currency: string;
    amount: string;
  };
  iataCode?: string;
}

export interface LatLngDto {
  lat: number;
  lng: number;
}

export interface PoiDto {
  type: PoiTypeDto;
  position: LatLngDto;
  metadata?: PoiMetaData;
}

export interface IssuesDto {
  title: string;
  description: string;
  link: string;
  type?: string;
}

export type CardType = 'BACKGROUND' | 'SPLIT';
export type ProductDto = (FlightOfferDto | AccommodationOfferDto | AccommodationRoomPackAvailabilityDto | ActivityOfferDto | PackageOfferDto) & {
  productType: 'FLIGHT' | 'ACCOMMODATION' | 'ACCOMMODATION_ROOM_PACK' | 'ACTIVITY' | 'PACKAGE';
};

export type Suggestions = GenericBoard<SuggestionItemDto> & { renderMode?: 'AS_ROW' | 'AS_COLUMN' | 'ALWAYS_ROW' };
export type Destinations = GenericBoard<DestinationDto>;
export type LandingItems = GenericBoard<LandingItemDto>;
export type BackgroundImages = GenericBoard<ImageDto>;
export type ProductOffers = GenericBoard<ProductDto>;
export type FlightPriceTrend = GenericBoard<FlightPriceTrendDto>;
export type DateSuggestions = GenericBoard<DateSuggestionsDto>;
export type Reels = GenericBoard<VideoDto>;
export type Cards = GenericBoard<CardDto> & { type: CardType };
export type GenericCards = GenericBoard<GenericCardDto>;
export type TextList = GenericBoard<TextsDto>;
export type Issues = GenericBoard<IssuesDto>;
export type Pois = GenericBoard<PoiDto> & { fitBounds?: LatLngDto[]; mapId?: string; maxZoom?: number; minZoom?: number };
export type ReservationDetail = GenericBoard<ReservationDetailDto>;
export type SpecialRequest = GenericBoard<SpecialRequestDto>;
export type AftersaleApi = GenericBoard<AftersaleAPIDto>;
export type TripDays = GenericBoard<TripDaysDto>;

export type AllDtos =
  | Suggestions
  | Destinations
  | BackgroundImages
  | ProductOffers
  | FlightPriceTrend
  | DateSuggestions
  | LandingItems
  | Reels
  | Cards
  | GenericCards
  | TextList
  | Issues
  | Pois
  | ReservationDetail
  | SpecialRequest
  | AftersaleApi
  | TripDays;

/**
 * @deprecated
 * Hay que eliminarlo cuando terminen de adaptar las tool remotas
 */
export interface MessageBoardDto {
  backgroundImages?: BackgroundImages;
  suggestion?: Suggestions;
  destinations?: Destinations;
  productOffers?: ProductOffers;
  flightPriceTrend?: FlightPriceTrend;
  dateSuggestions?: DateSuggestions;
  landingItems?: LandingItems;
  videos?: Reels;
  cards?: Cards;
  textList?: TextList;
  issues?: Issues;
  pois?: Pois;
  reservationDetail?: ReservationDetail;
  specialRequest?: SpecialRequest;
  aftersaleApi?: AftersaleApi;
  tripDays?: TripDays;
}

// Objeto de imagen
export interface ImageDto {
  url: string;
}

export interface ImageTypeDto {
  type: 'URL' | 'ID' | 'GOOGLE_ID';
  image: string;
}

export enum ButtonType {
  HREF = 'HREF',
  PROMPT = 'PROMPT',
  ACTION = 'ACTION',
}

interface BaseButtonDto {
  label: string;
  icon?: string;
  size?: EvaButtonSize;
  variant?: EvaButtonVariant;
}

export interface HrefButtonDto extends BaseButtonDto {
  type: ButtonType.HREF;
  link: string;
}

export interface PromptButtonDto extends BaseButtonDto {
  type: ButtonType.PROMPT;
  prompt: string;
}

export interface ActionButtonDto extends BaseButtonDto {
  type: ButtonType.ACTION;
  actionId: string;
  params?: string;
}

export type ActionButtonApi = Omit<ActionButtonDto, 'label'>;

export interface TextsDto extends BoardPosition {
  h1?: string;
  h2?: string;
  h3?: string;
  h4?: string;
  h5?: string;
  h6?: string;
  body1?: string;
  body2?: string;
  caption?: string;
  overline1?: string;
  overline2?: string;
  p?: string;
}

// Respuesta del submit del mensaje
export interface MessageDto {
  id: string;
  text: string;
  timestamp: number;
  type: MessageType;
  variant?: 'REQUEST' | 'RESPONSE' | 'PROGRESS' | 'OUTBOUND';
  suggestions?: MessageSuggestionsDto[];
  context?: MessageContextDto;
  content?: MessageContentDto;
  redirectTo?: string;
}

// Información del usuario provista por la creación del chat
export interface UserInfoDto {
  firstName: string;
  pictureUrl: string;
  domain: string;
  locale: string;
  userId: string;
  temporalUserId?: string;
  externalUserId?: string;
}

export type PreviousChat = PreviousChatDto & { mode: 'QS' | 'SERVICE' | 'CHANGE_USER_CHAT' };
// Información sobre chat previo
export interface PreviousChatDto {
  chatId: string;
  timestamp?: number;
  userId: string;
}

// Respuesta de la creacion o recuperacion de chat
export interface ChatDto {
  id: string;
  readOnly: boolean;
  messages: MessageDto[];
  userInfo: UserInfoDto;
  initUserMessage?: string;
  previousChat?: PreviousChatDto;
}

export interface ReservationDetailDto {
  product: string;
  name: string;
  status: string;
  shopping_card_id: string;
  reservation_code?: string;
  dates: string;
  link?: string;
  disclaimer?: string;
}

export interface AftersaleAPIDto {
  infoCard?: InfoCard;
  reschedulingCard?: ReschedulingCardResponse;
  webCheckIn?: WebCheckInDto;
  cdcInfo?: CdcInfo;
}

export interface InfoCard {
  header: InfoCardHeader;
  body: InfoCardBody;
  action?: InfoCardAction;
}

export interface InfoCardHeader {
  title: string;
  subtitle?: string;
  remark?: string;
  airlineInfo?: AirlineInfo;
}

export interface InfoCardBody {
  elements: InfoCardElement[];
  subtitle?: string;
  remark?: string;
}

export interface InfoCardElement {
  title: string;
  content: string;
  icon?: string;
  clipboard: boolean;
}

export interface InfoCardAction {
  text: string;
  link?: string;
  icon?: string;
}

export interface AirlineInfo {
  iconUrl: string;
  name: string;
  number: string;
}

export interface SelectItem {
  value: string;
  text: string;
}

export interface SpecialRequestDto {
  rooms: SelectItem[];
  items: SelectItem[];
}

export interface FeedbackMessageDto {
  message: string;
}

export interface FeedbackFormDto {}

export interface CheckoutDto {
  url: string;
}

export interface DriverDto {
  title: string;
  variant: string;
  icon?: string;
  tooltip?: DriverTooltipDto;
}

interface DriverTooltipDto {
  title?: string;
  description: string;
}

export interface FlightCityInfo {
  code: string;
  name: string;
  airport: string;
}

export interface FlightDateTime {
  date: string;
  time: string;
  plusDays?: number;
}

export interface DetailedFlightSegmentDto {
  origin: FlightCityInfo;
  destination: FlightCityInfo;
  departureDatetime: FlightDateTime;
  arrivalDatetime: FlightDateTime;
  duration: string;
  airline: string;
  cabinType: string;
  flightId: string;
  aircraft?: string;
  cityName: string;
  airportName: string;
  waitTime?: string;
  changePlane: boolean;
}

export interface FlightSegmentDto {
  type: 'DEPARTURE' | 'RETURNING';
  departureAirport: {
    iata: string;
    description: string;
  };
  departureDate: {
    date: string;
    time: string;
  };
  arrivalAirport: {
    iata: string;
    description: string;
  };
  arrivalDate: {
    date: string;
    time: string;
  };
  duration: string;
  stops: number;
  bags: {
    backpack: number;
    handbag: number;
    suitcase: number;
  };
  detailedSegments: DetailedFlightSegmentDto[];
}

export interface AirlineDto {
  code: string;
  description: string;
  logo: string;
  rating?: number;
}

interface TipLabel {
  text?: string;
  type?: '' | 'secondary' | 'benefit' | 'urgent';
}

export interface FlightOfferDto {
  id: string;
  stay?: string;
  link?: string;
  price: {
    amount: string;
    currency: string;
    description: string;
  };
  airlines: AirlineDto[];
  segments: FlightSegmentDto[];
  tipLabel?: TipLabel;
  favoriteInfo?: FlightFavoriteInfoDto;
  search: FlightsOfferBoardSearchParamsDto;
  checkoutAction?: ActionDto;
}

export interface FlightFavoriteInfoDto {
  externalId: string;
  itineraryId: string;
  url?: string;
  distribution: string;
  destination?: string;
  price: number;
  priceDate: string;
  currency: string;
  bagDistribution: string;
  airlines: string[];
  outboundSegment?: FlightFavoriteSegmentDto;
  returnSegment?: FlightFavoriteSegmentDto;
}

interface FlightFavoriteSegmentDto {
  checkIn: string;
  departureAirport: string;
  arrivalAirport: string;
  stopsQuantity: number;
}

export interface FlightsOfferBoardSearchParamsDto {
  originCode: string;
  destinationCode: string;
  departureDate: string;
  returnDate: string;
}

interface ActivityPriceDto {
  net?: string;
  gross: string;
  tax?: string;
  currency: string;
  text: string;
  originalPrice?: string;
  symbol: string;
}

interface ActivityPromotionDto {
  type: 'LOGGED_IN' | 'DISCOUNT_PERCENTAGE' | 'DISCOUNT_AMOUNT' | 'DISCOUNT' | 'PIG_DEAL';
  text: string;
}

interface ActivityReviewsDto {
  commentsInfo: {
    value: number;
    text: string;
  };
  score?: {
    value: number;
    text: string;
  };
}

export interface ActivityOfferDto {
  trackingId: string;
  title: string;
  description?: string;
  productType?: string;
  link: string;
  pickup: boolean;
  mainImage: string;
  images?: string[];
  price: ActivityPriceDto;
  promotion?: ActivityPromotionDto;
  drivers?: DriverDto[];
  reviewInformation: ActivityReviewsDto;
  favoriteInfo?: any;
}

export interface PackageOfferDto {
  packageId: string;
  title: string;
  duration: {
    dates: {
      from: string;
      to: string;
    };
    stay: {
      days: number;
      nights: number;
    };
  };
  image: ImageTypeDto;
  products: {
    accommodation: PackageAccommodationDto;
    flight: PackageFlightDto;
  };
  tags?: DriverDto[];
  price: PackagePriceDto;
  button: {
    label: string;
    link: string;
  };
  favoriteInfo?: any;
}

export interface PackageFlightDto {
  stops: number;
  originCityCode: string;
  destinationCityCode: string;
  bags: {
    backpack: boolean;
    handbag: boolean;
    suitcase: boolean;
  };
}

export interface PackageAccommodationDto {
  name: string;
  reviewScore?: number;
  stars: number;
}

export interface PackagePriceDto {
  symbol: string;
  description: string;
  amount: string;
  total: string;
  discount?: string;
}

export interface VideoDto {
  id: string;
  title: string;
  labels: string[];
  src: string[];
  picture: string;
}

export interface ChatListDto {
  label: string;
  chatids: ChatTitleDto[];
}

interface ChatTitleDto {
  id: string;
  title: string;
}

interface ClusterMarketingCarrier {
  code: string;
  name: string;
  logo: string;
}

interface ClusterAirport {
  code: string;
  name: string;
}

interface ClusterCity {
  name: string;
}

interface ClusterFrom {
  airport: ClusterAirport;
  city: ClusterCity;
}

interface ClusterTo {
  airport: ClusterAirport;
  city: ClusterCity;
}

interface ClusterDateTime {
  date: string;
  time: string;
}

export interface ClusterRouteSegment {
  marketing_carrier: ClusterMarketingCarrier;
  flight_number: string;
  departure: ClusterDateTime;
  arrival: ClusterDateTime;
  from: ClusterFrom;
  to: ClusterTo;
  duration: string;
  layover_duration: string;
  plus_days?: string;
}

export interface ClusterHeader {
  icon: string;
  title: string;
}

export interface ClusterRoute {
  from: ClusterFrom;
  to: ClusterTo;
  segments: ClusterRouteSegment[];
  duration: string;
  stops: number;
  plus_days?: string;
  header: ClusterHeader;
}

export interface ReschedulingCardConfirmationMessage {
  message: string;
  level: 'ERROR' | 'WARNING' | 'INFO' | 'SUCCESS';
}

export interface Cluster {
  routes: ClusterRoute[];
}

export interface ReschedulingCardFlight {
  id?: string;
  is_protected?: boolean;
  confirmation_message?: ReschedulingCardConfirmationMessage;
  link?: string;
  itinerary_type: string;
  cluster: Cluster;
}

export interface ActionAPI {
  actionId: string;
  label: string;
  params?: string;
}

export interface ReschedulingCardResponse {
  original?: ReschedulingCardFlight;
  alternative: ReschedulingCardFlight;
  action?: ActionAPI;
}

// Sofia Voice WS
export interface ConversationUpdate {
  type: string;
  item_id: string;
  role: 'assistant' | 'user';
  text: string; // TODO opcional
  audio: string; // TODO opcional
  suggestions: any[];
  boards: any[];
  response_type: 'delta' | 'done';
}

export interface ActionDto {
  actionId: string;
  label: string;
  params?: string;
}

export interface ProviderConfDevice {
  position: GlobaPosition;
  globaSize?: GlobaSize;
}

export interface ProviderConfAuth {
  authSystem: string;
  cookieLevel1: string;
  cookieLevel2?: string;
}

export interface ProviderFooter {
  termsAndConditionLink: string;
  poweredBy?: string;
  betaHighlight?: string;
}

export type Features =
  | 'previousChats'
  | 'recoverChat'
  | 'likeButton'
  | 'gallery'
  | 'productTour'
  | 'termsAndConditionsModal'
  | 'favourites'
  | 'userAvatar'
  | 'requestLocation'
  | 'downloadAppScreen';

export interface ProviderGlobaConf {
  globaImg: string;
  mobile: ProviderConfDevice;
  desktop: ProviderConfDevice;
}

export interface ProviderAsset {
  url: string;
  type: 'JS' | 'JS:MODULE' | 'CSS';
}

export type ProviderAssets = Array<ProviderAsset>;

export interface ProvidersConf {
  avatarImg: string | null;
  features: Array<Features>;
  overrideStyles?: { brandPrimary3: string; brandPrimary2: string };
  globa?: ProviderGlobaConf;
  auth?: ProviderConfAuth;
  initPreconditions?: Array<string>;
  footer: ProviderFooter;
  assets?: ProviderAssets;
  loginUrl: string;
}

export interface WebCheckInElement {
  title: string;
  content: string;
  icon?: string;
  clipboard: boolean;
}

export interface WebCheckInAction {
  text: string;
  link?: string;
  icon?: string;
}

export interface WebCheckInDto {
  destinationCityName: string;
  from: string;
  to: string;
  airlineCode: string;
  airlineName: string;
  flightNumber: string;
  title: string;
  items: WebCheckInElement[];
  action: WebCheckInAction;
}

export interface CdcInfo {
  cdcLink: string;
  webchatActive: boolean;
  whatsapp?: WhatsappInfo;
  phone?: PhoneInfo;
  query?: QueryContactInfo;
}

export interface WhatsappInfo {
  link: string;
  delayText?: string;
  description: string;
}

export interface PhoneInfo {
  localNumber: string;
  exteriorNumber: string;
  delayText?: string;
  description: string;
}

export interface QueryContactInfo {
  title: string;
  description: string;
  btn_text: string;
  icon: string;
}

export interface TextWithIconDTO {
  text: string;
  icon?: string;
}

export interface PromptedButtonDTO {
  display_text: string;
  prompt: string;
  icon?: string;
  variant?: EvaButtonVariant;
}
